/** Procedural SFX profile for Web Audio synthesis */
export interface ProceduralProfile {
  /**
   * S143 / BL-138: `sine` and `triangle` added. The original `square` was the
   * sharpest possible synth voice (a hard-clipped pulse, infinite odd harmonics)
   * — the source of the "stays annoying" the owner flagged in PT-40. Sine has
   * NO harmonics (purest tone, kindest to the ear at density); triangle has
   * only weak odd harmonics (still has "character" but no fizz). The high-
   * frequency repeats live on these two now; square stays for the rare sounds
   * whose snap is the point (place/upgrade/confirm).
   */
  type: "square" | "sine" | "triangle" | "noise" | "chord";
  durationMs: number;
  freq?: number;
  /** End frequency for sweeps (rises from freq to freqEnd) */
  freqEnd?: number;
  freqs?: number[];
  gain?: number;
  decay?: "exp" | "linear";
  /** Random detune range (cents) applied per play for anti-fatigue variation. */
  detuneRange?: number;
  /**
   * BL-120 (D-233): optional one-pole low-pass cutoff (Hz) applied at synthesis
   * time. Used to *round* the bright broadband noises (enemy death, leak,
   * overload, big detonations) into low "weight" rather than top-end hiss — the
   * "frequent gets rounder/lower" half of the AUDIO_BIBLE contrast concept.
   * Render-side only; ignored by the headless sim (determinism-neutral).
   */
  lowpass?: number;
}

/**
 * Procedural SFX profiles — used as fallback when WAV files are empty/missing.
 *
 * BL-120 / D-233 (the AUDIO_BIBLE SFX concept, PT-38 #4): the palette is tuned
 * for *contrast*, not loudness — the **frequent** sounds (tower fire, hits,
 * death, economy, UI) are pulled DOWN in register and gain and rounded, so the
 * ear isn't fed a constant wall of bright pings; the **rare** moments (boss
 * defeat, victory, big detonation) keep weight + an earned high sparkle. The
 * high register (>~1.2 kHz) is reserved for the things that should stand out.
 * See docs/design/AUDIO_BIBLE.md §2–3 for the per-family rationale. Audio is a
 * pure render-side EventBus consumer — none of this touches the sim or gs.rng.
 */
export const SFX_PROFILES: Record<string, ProceduralProfile> = {
  // — OPERATOR: your hand on the terminal — warm, rounded, low-fatigue —
  //
  // S143 / BL-138: the storm-frequency tonals (tower_shoot, the per-element
  // fire/hit pings, economy, ui_hover) moved from `square` (hard-clipped, the
  // owner's "stays annoying") to `sine`/`triangle`. Sine has no harmonics →
  // forms a clean texture at density; triangle has weak odd harmonics →
  // retains element character without the fizz. Place/upgrade/confirm keep
  // `square` because their snap is the point and they don't repeat.
  tower_shoot: {
    type: "triangle",
    durationMs: 70,
    freq: 360,
    gain: 0.1,
    decay: "exp",
    detuneRange: 45,
  },
  tower_place: {
    type: "square",
    durationMs: 120,
    freq: 440,
    gain: 0.2,
    decay: "linear",
  },
  tower_sell: {
    type: "square",
    durationMs: 100,
    freq: 330,
    gain: 0.18,
    decay: "exp",
  },
  tower_upgrade: {
    type: "square",
    durationMs: 150,
    freq: 660,
    gain: 0.2,
    decay: "linear",
  },
  // — FENRIR's pack: the things you shoot — death is a low "down", not a pop —
  enemy_death: {
    type: "noise",
    durationMs: 150,
    gain: 0.18,
    decay: "exp",
    detuneRange: 50,
    lowpass: 700,
  },
  // Task-011 (2026-gameplay-reworks, AC-14/O-6, D-325, Stage A): the ONLY
  // REPEAT_TIER tonal that was still `square` (the sharpest, harmonic-heaviest
  // type — everything else this frequent moved to `sine`/`triangle` per the
  // header note above). That mismatch, PLUS having no `detuneRange` at all
  // (unlike every sibling repeat-tier tonal), is exactly why mass spawns
  // stacked into an identical, harsh, un-varied buzz. `triangle` (soft odd
  // harmonics, no fizz) + a `detuneRange` in line with siblings (`combo_milestone`
  // 12, `enemy_death` 50) rounds the register one notch AND gives per-play
  // pitch variation for free (`AudioEngine.playSfx` already applies
  // `detuneRange` — see `:833-837` there — this profile just had to ask).
  enemy_spawn: {
    type: "triangle",
    durationMs: 60,
    freq: 220,
    gain: 0.08,
    decay: "exp",
    detuneRange: 30,
  },
  enemy_leak: {
    type: "noise",
    durationMs: 250,
    freq: 150,
    gain: 0.22,
    decay: "linear",
    lowpass: 500,
  },
  wave_start: {
    type: "chord",
    durationMs: 300,
    freqs: [330, 415, 494],
    gain: 0.25,
    decay: "linear",
  },
  wave_complete: {
    type: "chord",
    durationMs: 400,
    freqs: [523, 659, 784],
    gain: 0.28,
    decay: "linear",
  },
  // S195/BL-281 — the combo-milestone sting (the cue BL-241's banner comment
  // promised). A bright major-third rise a fifth above the wave chord's root
  // so it reads as REWARD, not completion; short + repeat-tier routed (ducked
  // under any boss/critical cue) so a milestone can never fight a marquee.
  combo_milestone: {
    type: "chord",
    durationMs: 240,
    freqs: [784, 988],
    gain: 0.22,
    decay: "exp",
    detuneRange: 12,
  },
  card_equip: {
    type: "chord",
    durationMs: 200,
    freqs: [440, 554, 659],
    gain: 0.3,
    decay: "linear",
  },
  card_equip_common: {
    type: "chord",
    durationMs: 180,
    freqs: [330, 415, 494],
    gain: 0.25,
    decay: "linear",
  },
  card_equip_rare: {
    type: "chord",
    durationMs: 220,
    freqs: [440, 554, 659],
    gain: 0.28,
    decay: "linear",
  },
  card_equip_epic: {
    type: "chord",
    durationMs: 260,
    freqs: [494, 622, 740],
    gain: 0.3,
    decay: "linear",
  },
  card_equip_legendary: {
    type: "chord",
    durationMs: 320,
    freqs: [523, 659, 784, 988],
    gain: 0.32,
    decay: "linear",
  },
  game_over: {
    type: "chord",
    durationMs: 500,
    freqs: [220, 165, 131],
    gain: 0.35,
    decay: "linear",
  },
  boss_stinger: {
    type: "chord",
    durationMs: 400,
    freqs: [110, 138, 165],
    gain: 0.35,
    decay: "exp",
  },
  // — UI / system: tactile, quiet, lower register —
  // S147 (owner): a soft "glass" button click — a short bell-like ping (two
  // high, slightly-inharmonic partials, exp decay, 6 ms soft onset) at low gain.
  ui_click: {
    type: "chord",
    durationMs: 75,
    freqs: [1950, 2930],
    gain: 0.05,
    decay: "exp",
    detuneRange: 20,
  },
  countdown_beep: {
    type: "sine",
    durationMs: 80,
    freq: 440,
    gain: 0.18,
    decay: "exp",
  },
  wave_boot: {
    type: "chord",
    durationMs: 250,
    freqs: [220, 440, 660],
    gain: 0.28,
    decay: "linear",
  },
  // — VERA / DEFENSE: the legible lane; minor hits softer, criticals keep bite —
  mainframe_hit_small: {
    type: "square",
    durationMs: 100,
    freq: 820,
    gain: 0.16,
    decay: "exp",
  },
  mainframe_hit_medium: {
    type: "noise",
    durationMs: 200,
    gain: 0.25,
    decay: "exp",
    lowpass: 1600,
  },
  // Task-003 (2026-audio-module-audit, AC-4) outlier retune: gain 0.3 → 0.44.
  // The tight 600 Hz lowpass on a noise source crushes RMS far more than the
  // gain constant alone suggests — measured 12 dB quieter than its
  // SFX_CRITICAL peers (see AudioLoudness.ts's module doc). One-line,
  // gain-only tune (D-244 pattern); envelope/filter/duck behavior unchanged.
  mainframe_hit_heavy: {
    type: "noise",
    durationMs: 400,
    freq: 80,
    gain: 0.44,
    decay: "linear",
    lowpass: 600,
  },
  mainframe_hit_critical: {
    type: "chord",
    durationMs: 600,
    freqs: [55, 82, 110],
    gain: 0.35,
    decay: "linear",
  },
  heartbeat: {
    type: "square",
    durationMs: 150,
    freq: 40,
    gain: 0.15,
    decay: "exp",
  },
  tower_destroyed: {
    type: "noise",
    durationMs: 450,
    freq: 60,
    gain: 0.35,
    decay: "exp",
    lowpass: 700,
  },
  tower_destroyed_crash: {
    type: "chord",
    durationMs: 350,
    freqs: [55, 82, 110],
    gain: 0.3,
    decay: "exp",
  },
  signal_overload: {
    type: "noise",
    durationMs: 500,
    freq: 2200,
    gain: 0.3,
    decay: "linear",
    lowpass: 1400,
  },
  // Task-003 (2026-audio-module-audit, AC-4) outlier retune: gain 0.2 → 0.15.
  // A sustained square wave (near-unity RMS-to-peak, unlike noise/sine/chord)
  // measured LOUDER than most SFX_CRITICAL members despite being the
  // secondary "hum" layer of an already-critical alarm (see AudioLoudness.ts's
  // module doc). One-line, gain-only tune (D-244 pattern); envelope/duck
  // behavior unchanged.
  signal_overload_hum: {
    type: "square",
    durationMs: 800,
    freq: 60,
    gain: 0.15,
    decay: "linear",
  },
  path_stream: {
    type: "square",
    durationMs: 2000,
    freq: 100,
    freqEnd: 600,
    gain: 0.18,
    decay: "linear",
  },
  path_distortion: {
    type: "noise",
    durationMs: 120,
    freq: 400,
    gain: 0.25,
    decay: "exp",
    lowpass: 1200,
  },
  payload_kernelPanic: {
    type: "noise",
    durationMs: 500,
    gain: 0.3,
    decay: "exp",
    lowpass: 1200,
  },
  payload_reduceMaxHp: {
    type: "square",
    durationMs: 300,
    freq: 260,
    freqEnd: 120,
    gain: 0.22,
    decay: "exp",
  },

  // Per-element tower fire signatures (BL-68 P3 · retuned BL-120): each keeps
  // its element identity but drops register + gain into the low-mid "body" band
  // so a storm of them is a texture, not a wall of bright pings.
  tower_fire_fire: {
    type: "noise",
    durationMs: 60,
    freq: 800,
    gain: 0.11,
    decay: "exp",
    detuneRange: 40,
    lowpass: 1100,
  },
  tower_fire_water: {
    type: "chord",
    durationMs: 90,
    freqs: [200, 300, 500],
    gain: 0.1,
    decay: "exp",
    detuneRange: 30,
  },
  tower_fire_lightning: {
    type: "triangle",
    durationMs: 55,
    freq: 620,
    freqEnd: 240,
    gain: 0.1,
    decay: "exp",
    detuneRange: 45,
  },
  tower_fire_signal: {
    type: "triangle",
    durationMs: 45,
    freq: 520,
    gain: 0.085,
    decay: "exp",
    detuneRange: 25,
  },
  tower_fire_virus: {
    type: "noise",
    durationMs: 70,
    freq: 200,
    gain: 0.1,
    decay: "exp",
    detuneRange: 35,
    lowpass: 900,
  },
  tower_fire_dark: {
    type: "chord",
    durationMs: 80,
    freqs: [55, 82, 110],
    gain: 0.1,
    decay: "exp",
    detuneRange: 30,
  },

  // Per-element projectile hit (BL-68 P3 · retuned BL-120): pulled off the top
  // of the register; lightning/signal keep their "electric" snap low-mid.
  hit_fire: {
    type: "noise",
    durationMs: 100,
    freq: 600,
    gain: 0.16,
    decay: "exp",
    detuneRange: 40,
    lowpass: 1400,
  },
  hit_water: {
    type: "noise",
    durationMs: 120,
    freq: 300,
    gain: 0.14,
    decay: "exp",
    detuneRange: 30,
    lowpass: 1000,
  },
  hit_lightning: {
    type: "triangle",
    durationMs: 60,
    freq: 760,
    freqEnd: 320,
    gain: 0.11,
    decay: "exp",
    detuneRange: 45,
  },
  hit_signal: {
    type: "triangle",
    durationMs: 50,
    freq: 720,
    gain: 0.09,
    decay: "exp",
    detuneRange: 25,
  },
  hit_virus: {
    type: "noise",
    durationMs: 150,
    freq: 150,
    gain: 0.14,
    decay: "linear",
    detuneRange: 35,
    lowpass: 800,
  },
  hit_dark: {
    type: "chord",
    durationMs: 130,
    freqs: [65, 98],
    gain: 0.14,
    decay: "exp",
    detuneRange: 30,
  },

  // — FENRIR boss: the rare theatre — spawn/phase/defeat earn their weight —
  boss_spawn: {
    type: "chord",
    durationMs: 600,
    freqs: [82, 110, 165],
    gain: 0.35,
    decay: "linear",
  },
  boss_phase: {
    type: "chord",
    durationMs: 400,
    freqs: [110, 165, 220],
    gain: 0.3,
    decay: "exp",
  },
  // Defeat = a reward: a low root added under the rising chord for body.
  boss_defeat: {
    type: "chord",
    durationMs: 900,
    freqs: [110, 220, 330, 440, 660],
    gain: 0.35,
    decay: "linear",
  },

  // Victory fanfare (BL-68 P3 · BL-120): the one big bright moment — the same
  // resolve, now with a sub root (110) for weight under the 880 sparkle.
  victory_fanfare: {
    type: "chord",
    durationMs: 1100,
    freqs: [110, 330, 440, 554, 660, 880],
    gain: 0.35,
    decay: "linear",
  },

  // UI sound kit. S147 (owner): hover + click are a soft, dezent "glass" tick —
  // a short bell-like ping (two high, slightly-inharmonic partials + exp decay +
  // a 6 ms soft onset), hover the lightest/quietest. Supersedes the BL-138 sine
  // for these two: they are user-paced (not storm-frequency like economy_tick /
  // countdown_beep, which stay sine), and the gain is low enough that the
  // density-fatigue concern does not apply.
  ui_hover: {
    type: "chord",
    durationMs: 55,
    freqs: [2740, 4110],
    gain: 0.022,
    decay: "exp",
    detuneRange: 30,
  },
  ui_confirm: {
    type: "square",
    durationMs: 50,
    freq: 820,
    gain: 0.12,
    decay: "exp",
  },
  ui_error: {
    type: "noise",
    durationMs: 200,
    freq: 300,
    gain: 0.2,
    decay: "linear",
    lowpass: 900,
  },
  ui_open: {
    type: "square",
    durationMs: 60,
    freq: 600,
    freqEnd: 900,
    gain: 0.1,
    decay: "exp",
  },
  ui_close: {
    type: "square",
    durationMs: 60,
    freq: 900,
    freqEnd: 600,
    gain: 0.1,
    decay: "exp",
  },

  // Game moments (BL-68 P4)
  skill_activate: {
    type: "chord",
    durationMs: 250,
    freqs: [440, 660, 880],
    gain: 0.22,
    decay: "exp",
  },
  firewall_place: {
    type: "noise",
    durationMs: 150,
    freq: 200,
    gain: 0.2,
    decay: "linear",
    lowpass: 1200,
  },
  firewall_sell: {
    type: "square",
    durationMs: 100,
    freq: 400,
    freqEnd: 300,
    gain: 0.15,
    decay: "exp",
  },
  firewall_destroy: {
    type: "noise",
    durationMs: 350,
    freq: 100,
    gain: 0.28,
    decay: "linear",
    lowpass: 800,
  },
  payload_arm: {
    type: "square",
    durationMs: 200,
    freq: 300,
    freqEnd: 600,
    gain: 0.15,
    decay: "linear",
  },
  payload_fire: {
    type: "noise",
    durationMs: 300,
    freq: 500,
    gain: 0.25,
    decay: "exp",
    lowpass: 1400,
  },
  // Detonate = the weight moment — rounded into a low boom.
  payload_detonate: {
    type: "noise",
    durationMs: 500,
    freq: 80,
    gain: 0.42,
    decay: "linear",
    lowpass: 420,
  },
  achievement_unlock: {
    type: "chord",
    durationMs: 500,
    freqs: [523, 659, 784, 1047],
    gain: 0.3,
    decay: "linear",
  },
  synergy_trigger: {
    type: "chord",
    durationMs: 200,
    freqs: [440, 660, 880],
    gain: 0.18,
    decay: "exp",
    detuneRange: 20,
  },
  // Economy: fires on every credit — softened + lowered + detuned so it stops
  // being the constant 1.8 kHz chirp the owner flagged. S143 / BL-138: now a
  // sine (no harmonics) so a streak of them is a soft pulse instead of a chirp
  // train.
  economy_tick: {
    type: "sine",
    durationMs: 28,
    freq: 520,
    gain: 0.035,
    decay: "exp",
    detuneRange: 30,
  },
  pause_in: {
    type: "square",
    durationMs: 100,
    freq: 500,
    freqEnd: 300,
    gain: 0.12,
    decay: "linear",
  },
  pause_out: {
    type: "square",
    durationMs: 100,
    freq: 300,
    freqEnd: 500,
    gain: 0.12,
    decay: "linear",
  },
  time_warp: {
    type: "square",
    durationMs: 400,
    freq: 200,
    freqEnd: 1200,
    gain: 0.2,
    decay: "exp",
  },
  // BL-70: per-payload audio signatures.
  payload_gcForce: {
    type: "square",
    durationMs: 250,
    freq: 600,
    freqEnd: 200,
    gain: 0.25,
    decay: "exp",
  },
  payload_logicBomb_arm: {
    type: "square",
    durationMs: 200,
    freq: 880,
    freqEnd: 660,
    gain: 0.15,
    decay: "linear",
  },
  payload_logicBomb: {
    type: "noise",
    durationMs: 350,
    gain: 0.45,
    decay: "exp",
    lowpass: 1600,
  },
  payload_backdoor: {
    type: "square",
    durationMs: 400,
    freq: 200,
    freqEnd: 800,
    gain: 0.3,
    decay: "exp",
  },
  payload_forkBomb: {
    type: "square",
    durationMs: 300,
    freq: 440,
    freqEnd: 220,
    gain: 0.25,
    decay: "exp",
  },
  payload_pidHijack: {
    type: "square",
    durationMs: 450,
    freq: 150,
    freqEnd: 50,
    gain: 0.4,
    decay: "exp",
  },
};
